import { useEffect, useMemo, useState } from 'react';
import { useStatefulAsyncEffect } from 'packages/lib/utils/hooks/use-stateful-async-effect';
import {
  getTokenSilently,
  user,
  userScopeIncludesOneOf,
} from 'packages/lib/auth';
import { User } from 'packages/lib/auth/data';
import { useCurrentUser } from 'packages/roster-man/api/use-users';
import { getUserToolRole, PermissionRole } from 'packages/lib/auth/permissions';
import { useQuery } from 'packages/lib/utils/hooks/use-query';
import { isActiveUser } from 'packages/lib/auth/utils';

interface UseUserProfileReturns {
  loadingUserProfile: boolean;
  userProfileError: unknown;
  userProfile: User | null;
}

export const useUserProfile = (): UseUserProfileReturns => {
  const { loading, error, data } = useStatefulAsyncEffect(user, []);

  return {
    loadingUserProfile: loading,
    userProfileError: error,
    userProfile: data,
  };
};

interface UseAccessTokenReturns {
  loadingAccessToken: boolean;
  accessTokenError: unknown;
  accessToken: string | null;
}

export const useAccessToken = (): UseAccessTokenReturns => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<null | unknown>(null);
  const [data, setData] = useState<null | string>(null);
  useEffect(
    () => {
      let mounted = true;
      getTokenSilently()
        .then((res) => {
          if (!mounted) return;
          setData(res?.accessToken || '');
        })
        .catch((err) => {
          if (!mounted) return;
          setError(err);
        })
        .finally(() => {
          if (!mounted) return;
          setLoading(false);
        });
      return () => {
        mounted = false;
      };
    } /* every */
  );

  return {
    loadingAccessToken: loading,
    accessTokenError: error,
    accessToken: data,
  };
};

interface UseHasWritePermissionReturns {
  loadingHasWritePermission: boolean;
  hasWritePermissionError: unknown;
  hasWritePermission: boolean | null;
}

export const useHasWritePermission = (): UseHasWritePermissionReturns => {
  const { loading, error, data } = useStatefulAsyncEffect(
    () => userScopeIncludesOneOf(['write:annotations']),
    []
  );
  const search = useQuery();
  const usingWorkforcePerms = search.get('workforce-perms') === 'true';
  const { data: currentUser } = useCurrentUser();
  const userHasPermission = useMemo(() => {
    return currentUser
      ? getUserToolRole(currentUser, 'annotations') !== PermissionRole.View
      : false;
  }, [currentUser]);
  const userIsActive = isActiveUser(currentUser);
  return {
    loadingHasWritePermission: loading,
    hasWritePermissionError: error,
    hasWritePermission: usingWorkforcePerms
      ? userHasPermission && userIsActive
      : data,
  };
};
